/**
 * This file is shared between pok-core & pok-web
 * If you have to change it then do it in pok-core and run yarn get:validation-schemas @pok-web
 */

// A pixel counts as content when it is visible and not (almost) white.
const MIN_ALPHA = 10;
const MAX_LUMINANCE = 235;
// Rows / columns with fewer content pixels are treated as scan specks.
const MIN_CONTENT_PIXELS = 2;
// Margin kept around the content, as a fraction of the content size.
const MARGIN = 0.02;

export interface ImageContentBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

const contentRange = (counts: number[]) => {
  const first = counts.findIndex(count => count >= MIN_CONTENT_PIXELS);
  if (first < 0) {
    return undefined;
  }
  let last = counts.length - 1;
  while (counts[last] < MIN_CONTENT_PIXELS) {
    last--;
  }
  return { first, last };
};

/**
 * Box around the drawing in an RGBA image (4 bytes per pixel), with a small
 * margin, used to cut empty margins off stamps and signatures.
 * Returns undefined when there is nothing to cut (no content, or the content
 * already fills the image).
 */
export const findImageContentBox = (
  rgba: Uint8Array | Uint8ClampedArray,
  width: number,
  height: number,
): ImageContentBox | undefined => {
  const rowCounts = new Array<number>(height).fill(0);
  const columnCounts = new Array<number>(width).fill(0);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const luminance =
        0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2];
      if (rgba[i + 3] > MIN_ALPHA && luminance < MAX_LUMINANCE) {
        rowCounts[y]++;
        columnCounts[x]++;
      }
    }
  }

  const rows = contentRange(rowCounts);
  const columns = contentRange(columnCounts);
  if (!rows || !columns) {
    return undefined;
  }

  const marginX = Math.round((columns.last - columns.first + 1) * MARGIN);
  const marginY = Math.round((rows.last - rows.first + 1) * MARGIN);
  const left = Math.max(0, columns.first - marginX);
  const top = Math.max(0, rows.first - marginY);
  const right = Math.min(width - 1, columns.last + marginX);
  const bottom = Math.min(height - 1, rows.last + marginY);
  const box = {
    left,
    top,
    width: right - left + 1,
    height: bottom - top + 1,
  };
  return box.width === width && box.height === height ? undefined : box;
};
